import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Phone, CheckCircle, ArrowLeft, Eye } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientPortalAccessPage() {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [demoToken, setDemoToken] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    axios.get(`${API}/api/public/demo-portal`).then((r) => setDemoToken(r.data.token)).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Just your name and phone number is all we need"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/request-care`, {
        ...form,
        who_for: "Client portal access",
        message: `Client portal link request. ${form.message}`.trim(),
      });
      setSent(true);
    } catch { toast.error("Something went wrong — please call us at 800-719-6912"); }
    setBusy(false);
  };

  const inputCls = "w-full rounded-md border border-stone-300 bg-white px-4 py-3 text-base text-[#23282f] placeholder:text-stone-400 focus:border-[#6A3E97] focus:outline-none focus:ring-2 focus:ring-[#6A3E97]/20 transition-colors";

  return (
    <div className="min-h-screen bg-[#f6f7f9] font-sans text-[#23282f]" data-testid="client-portal-page">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/"><img src="/shc-logo.png" alt="Silver Home Care" className="h-12 w-auto" /></Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-600 hover:text-[#6A3E97]" data-testid="portal-back-home">
            <ArrowLeft size={15} /> Back to site
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <h1 className="font-heading text-3xl font-medium tracking-tight sm:text-4xl" data-testid="portal-headline">Client & family portal</h1>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-stone-600">
          The portal is how families see visit updates and stay in touch with our office.
          Access works through a private link we send you — by email or text — when care begins.
        </p>

        <div className="mt-8 rounded-xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8">
          {demoToken && (
            <Link to={`/portal/${demoToken}`}
              className="flex items-center justify-between gap-4 rounded-lg border border-[#6A3E97]/25 bg-[#6A3E97]/5 px-5 py-4 transition-colors hover:bg-[#6A3E97]/10"
              data-testid="portal-demo-open">
              <span>
                <span className="flex items-center gap-2 font-heading text-lg font-semibold text-[#23282f]"><Eye size={19} className="text-[#6A3E97]" /> See a demo portal</span>
                <span className="mt-0.5 block text-sm text-stone-600">Look around a sample family portal — no sign-in, nothing real.</span>
              </span>
              <span className="shrink-0 text-sm font-semibold text-[#6A3E97]">Open →</span>
            </Link>
          )}
          <h2 className="mt-7 font-heading text-xl font-semibold tracking-tight">Already have your link?</h2>
          <p className="mt-2 text-sm leading-relaxed text-stone-600">
            Open the email or text from our office and tap the portal link — no password needed.
            Check your spam folder if you don't see it.
          </p>
          <div className="mt-5 rounded-lg border border-stone-200 bg-stone-50 p-5">
            <p className="text-sm font-medium text-[#23282f]">Is your portal email a Google account?</p>
            <p className="mt-1 text-sm leading-relaxed text-stone-600">Sign in with Google and we'll open your portal — no link hunting needed.</p>
            <button
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + "/client-portal";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="mt-3 inline-flex items-center gap-2 rounded-md border border-stone-300 bg-white px-5 py-2.5 text-sm font-semibold text-stone-800 shadow-sm transition-colors hover:bg-stone-50"
              data-testid="portal-google-signin">
              <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2 3.7-5 3.7-8.6z"/>
                <path fill="#34A853" d="M12 24c3.2 0 6-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-6-2.2-7-5.1l-3.9 3C3.1 21.3 7.2 24 12 24z"/>
                <path fill="#FBBC05" d="M5 14.3c-.2-.7-.4-1.5-.4-2.3s.2-1.6.4-2.3l-3.9-3C.4 8.2 0 10 0 12s.4 3.8 1.1 5.3l3.9-3z"/>
                <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.2 0 3.1 2.7 1.1 6.7l3.9 3c1-2.9 3.8-5 7-5z"/>
              </svg>
              Sign in with Google
            </button>
          </div>
          <h2 className="mt-7 font-heading text-xl font-semibold tracking-tight">Lost it, or never got one?</h2>
          <p className="mt-2 text-sm leading-relaxed text-stone-600">
            Call us and we'll send a fresh link right away:{" "}
            <a href="tel:8007196912" className="font-semibold text-[#6A3E97]" data-testid="portal-call"><Phone size={13} weight="fill" className="inline" /> 800-719-6912</a>{" "}
            — or send us your info and we'll get back to you, usually the same business day.
          </p>

          {sent ? (
            <div className="mt-6 rounded-lg border border-[#1e8449]/30 bg-[#1e8449]/5 p-5 text-center" data-testid="portal-request-success">
              <CheckCircle size={32} weight="fill" className="mx-auto text-[#1e8449]" />
              <p className="mt-2 font-semibold text-[#23282f]">Request received.</p>
              <p className="mt-1 text-sm text-stone-600">Someone from our office will send your portal link shortly.</p>
            </div>
          ) : (
            <div className="mt-5 space-y-4" data-testid="portal-request-form">
              <input value={form.name} onChange={set("name")} placeholder="Your name *" className={inputCls} data-testid="portal-req-name" />
              <input value={form.phone} onChange={set("phone")} type="tel" placeholder="Phone (or email) *" className={inputCls} data-testid="portal-req-phone" />
              <input value={form.message} onChange={set("message")} placeholder="Who is the client? (helps us find your record)" className={inputCls} data-testid="portal-req-client" />
              <button onClick={submit} disabled={busy}
                className="rounded-md bg-[#6A3E97] px-6 py-3 text-base font-semibold text-white shadow-sm transition-colors hover:bg-[#55317A] disabled:opacity-50"
                data-testid="portal-request-submit">
                {busy ? "Sending…" : "Send me my portal link"}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
